import React from 'react'
import styled from '@emotion/styled'
import { colors, font, shadow, spacing } from '@mobi/component-library/Theme/Common'
import { Icon } from '@mobi/component-library/Common/V2'
import { Last10WinLossIndicator } from '@core/Areas/FollowMe/Components/Common/Last10WinLossIndicator'
import { followMePrincipleProfileStatsQuery } from '@core/Areas/FollowMe/helpers/queries'
import { useQuery } from '@tanstack/react-query'
import { FollowMeProfile } from '@core/Areas/FollowMe/types'
import { toMoney } from '@mobi/utils/money'

export const ProfileStats = ({ id }: Pick<FollowMeProfile, 'id'>) => {
  const { data } = useQuery(followMePrincipleProfileStatsQuery(id))

  return (
    <WrapperStyled>
      <div>
        <div>
          <span>Last 10</span>
          <span>Shared Bets</span>
        </div>
        <Last10WinLossIndicator last10Results={data?.lastTenResults ?? []} size='l' />
      </div>

      <div>
        <div className='profile-stats__list-item'>
          <Icon name='DuocolorTrophy01' size='2rem' color='black' />
          <dl>
            <dt>Total Wins</dt>
            <dd>All Time</dd>
          </dl>
          <span className='profile-stats__stat-value'>{data?.totalWins}</span>
        </div>

        <div className='profile-stats__list-item'>
          <Icon name='CustomEllipse12' size='2rem' color='black' />
          <dl>
            <dt>Best Win Streak</dt>
            <dd>All Time</dd>
          </dl>
          <span className='profile-stats__stat-value'>{data?.bestWinStreak}</span>
        </div>

        <div className='profile-stats__list-item'>
          <Icon name='DuocolorLineChartUp01' size='2rem' color='black' />
          <dl>
            <dt>Average Win Odds</dt>
            <dd>All Time</dd>
          </dl>
          <span className='profile-stats__stat-value'>
            {data?.avgOdds ? toMoney(data.avgOdds) : '0'}
          </span>
        </div>

        <div className='profile-stats__list-item'>
          <Icon name='DuocolorAward03' size='2rem' color='black' />
          <dl>
            <dt>Biggest Win Odds</dt>
            <dd>All Time</dd>
          </dl>
          <span className='profile-stats__stat-value'>
            {data?.highestOddsWin ? toMoney(data.highestOddsWin) : '0'}
          </span>
        </div>

        <div className='profile-stats__list-item'>
          <Icon name='DuocolorCopy02' size='2rem' color='black' />
          <dl>
            <dt>Total Copies</dt>
            <dd>All Time</dd>
          </dl>
          <span className='profile-stats__stat-value'>{data?.copyCount}</span>
        </div>
      </div>
    </WrapperStyled>
  )
}

// Styles
const WrapperStyled = styled.div({
  padding: `${spacing.smx1} ${spacing.md}`,

  // last 10
  '> div:first-of-type': {
    background: colors.white,
    padding: spacing.md,
    marginBottom: '0.6rem',
    border: `1px solid ${colors.neutral[200]}`,
    borderRadius: spacing.md,
    boxShadow: shadow.xs,

    '> div:first-of-type': {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: spacing.sm,

      '& > span': {
        fontFamily: font.family.primary,
        fontSize: font.size.md.fontSize,
      },

      '& > span:first-of-type': {
        fontWeight: font.weight.semibold,
      },

      '& > span:last-of-type': {
        fontWeight: font.weight.regular,
        color: colors.neutral[800],
      },
    },
  },

  // list container
  '> div:nth-of-type(2)': {
    boxShadow: shadow.sm,
    borderRadius: spacing.md,
    overflow: 'hidden',

    '.profile-stats__list-item': {
      background: colors.white,
      padding: `${spacing.sm} ${spacing.md}`,
      borderBottom: `1px solid ${colors.neutral[200]}`,
      fontFamily: font.family.primary,

      display: 'flex',
      alignItems: 'center',
      gap: spacing.md,

      '&:first-of-type': {
        borderTopLeftRadius: spacing.md,
        borderTopRightRadius: spacing.md,
      },

      '&:last-of-type': {
        borderBottomLeftRadius: spacing.md,
        borderBottomRightRadius: spacing.md,
      },

      dl: {
        display: 'flex',
        flexDirection: 'column',
        margin: 0,

        dt: {
          fontWeight: font.weight.semibold,
          fontSize: font.size.lg.fontSize,
          letterSpacing: font.size.lg.letterSpacing,
        },

        dd: {
          margin: 0,
          fontSize: font.size.md.fontSize,
          letterSpacing: font.size.md.letterSpacing,
          lineHeight: font.size.md.lineHeight,
          color: colors.neutral[800],
        },
      },

      // stat value
      '> span:last-of-type': {
        marginLeft: 'auto',
        fontWeight: font.weight.black,
        color: colors.neutral[900],
        fontFamily: font.family.primary,
        fontSize: font.size.xl4.fontSize,
        letterSpacing: font.size.xl4.letterSpacing,
        fontStyle: 'italic',
        textAlign: 'center',
      },
    },
  },
})
